import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AccountStatus } from "@app/enum/account/AccountStatus";
import { Platform } from "@app/enum/account/Platform";
import { IAccount } from "@app/services/account.service";
import { AccountFeatureService, IAccountFeatureDescription } from "@app/services/accountFeature.service";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { CurrentUserService } from "@app/services/currentUser.service";
import { Observable, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { IReseller } from "../_models/IReseller";

@Injectable({
    providedIn: "root"
})
export class AccountService {
    private customerAccountsCacheName: string = "customerAccountsCache";
    private resellerAccountsCacheName: string = "resellerAccountsCache";

    constructor(
        private readonly http: HttpClient,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly dataCacheService: DataCacheService,
        private readonly currentUserService: CurrentUserService
    ) {
    }

    getCustomerAccount(id: number): Observable<IAccount> {
        const account = this.dataCacheService.getSingleValue(id, this.customerAccountsCacheName);

        if (account) {
            return of(account);
        }

        return this.http.get<IAccount>(`/api/foundation/accounts/${id}`);
    }

    getCustomerAccounts(invalidateCache = false): Observable<IAccount[]> {
        return this.dataCacheService.getOrRequest(this.customerAccountsCacheName, () => this.getCustomerAccountsFromDb(), { invalidateCache })
            .pipe(map(accounts => {
                return accounts.filter(a => a.Status != AccountStatus.Prospective)
            }));
    }

    getCustomerAccountsFromDb(): Observable<IAccount[]> {
        return this.http.get<IAccount[]>(`/api/foundation/accounts/`);
    }

    getResellers(invalidateCache = false): Observable<IReseller[]> {
        return this.dataCacheService.getOrRequest(this.resellerAccountsCacheName, () => this.getResellersFromDb(), { invalidateCache })
    }

    getResellersFromDb(): Observable<IReseller[]> {
        return this.http.get<IReseller[]>(`/api/foundation/accounts/resellers`);
    }

    createAccountMap(accounts: IAccount[]): Map<number, IAccount> {
        return new Map(accounts.map(a => [a.Id, a]));
    }

    updateFeatureSet(customerId: number): Observable<IAccountFeatureDescription[]> {
        return this.updateCustomerFeatures(customerId)
            .pipe(switchMap(() => this.getCustomerFeatureSet(customerId)))
    }

    updateCustomerFeatures(accountId: number): Observable<IAccount> {
        return this.http.post<IAccount>("/api/foundation/accounts/updatefeatures", Number(accountId));
    }

    getCustomerAccountImage(accountId: number, invalidateCache = false): Observable<string> {
        return this.http.get<string>(`/api/foundation/accounts/${accountId}/image`);
    }

    saveAccount(account: IAccount): Observable<IAccount> {
        if (!account) {
            return;
        }

        // Validate model

        account.Website = account.Website || "";
        account.Platform = account.Platform || Platform.Verilocation;

        return this.http.post<IAccount>("/api/foundation/accounts/", account).pipe(tap(account => {
            this.dataCacheService.addOrUpdateItem(account, this.customerAccountsCacheName);
        }));
    }

    getResellerName(resellers: IReseller[], resellerId: number): string {
        if (resellerId) {
            const resellerAccount = resellers.find(r => r.Id == resellerId);
            return resellerAccount?.Description || "Unknown";
        }

        return `AddSecure UK`;
    }

    private getCustomerFeatureSet(customerId: number): Observable<IAccountFeatureDescription[]> {
        return this.getCustomerAccount(customerId)
            .pipe(switchMap(
                customer => {
                    return this.refreshFeatureSet(customer);
                }))
    }

    private refreshFeatureSet(customer, includeDisabled = false): Observable<IAccountFeatureDescription[]> {
        let featureSet: IAccountFeatureDescription[] = this.accountFeatureService.getFeatureDescriptionsForAccount(customer);
        if (includeDisabled) {
            return of(featureSet);
        }
        else {
            return of(featureSet.filter(f => f.enabled));
        }
    }
}
